/**
 * Thermal Printer Configuration
 *
 * This file contains configurable settings for thermal printer receipts.
 * You can modify these settings based on your printer model and requirements.
 */

import brandingConfig from './brandingConfig';

export const PRINTER_CONFIGS = {
  // Standard 80mm thermal printer (most common)
  THERMAL_80MM: {
    name: '80mm Thermal Printer',
    width: '80mm',
    fontSize: '12px',
    lineHeight: '1.4',
    padding: '5mm',
    description: 'Standard thermal printer (POS printers, receipt printers)'
  },

  // Small 58mm thermal printer
  THERMAL_58MM: {
    name: '58mm Thermal Printer',
    width: '58mm',
    fontSize: '11px',
    lineHeight: '1.3',
    padding: '3mm',
    description: 'Compact thermal printer (mobile POS, portable printers)'
  },

  // Large 80mm with more spacing
  THERMAL_80MM_LARGE: {
    name: '80mm Large Font',
    width: '80mm',
    fontSize: '14px',
    lineHeight: '1.5',
    padding: '6mm',
    description: 'Standard printer with larger, easier-to-read text'
  }
};

// Default printer configuration (can be changed based on user preference)
export const DEFAULT_PRINTER_CONFIG = PRINTER_CONFIGS.THERMAL_58MM;

/**
 * Restaurant/Company Information
 * Customize this with your restaurant details
 */
export const COMPANY_INFO = {
  name: 'MEZBAAN RESTAURANT',
  subtitle: 'Premium Dining Service',
  address: '', // Optional: Add address if needed
  phone: '', // Optional: Add phone if needed
  gst: '', // Optional: Add GST number if needed
  footer: 'Thank You! Visit Again!',
  poweredBy: brandingConfig.printerPoweredBy
};

/**
 * Receipt Print Settings
 */
export const PRINT_SETTINGS = {
  // Auto-print delay (milliseconds)
  autoPrintDelay: 250,

  // Auto-close window after print
  autoCloseAfterPrint: true,
  autoCloseDelay: 100,

  // Show preview before printing (if false, prints immediately)
  showPreview: true,

  // Include payment details on receipt
  includePaymentDetails: true,

  // Include discount reason on receipt
  includeDiscountReason: true,

  // Auto-cut settings for thermal printers
  // Note: Auto-cut must be enabled in your thermal printer's settings/driver
  // The CSS page-break will trigger the printer's auto-cut mechanism
  enableAutoCut: true,

  // Add extra spacing at the end of receipt for cleaner cuts
  bottomMargin: '10mm'
};

/**
 * Supported Printer Models (for reference)
 * Common thermal printer models and their specifications
 */
export const SUPPORTED_PRINTERS = [
  {
    brand: 'Epson',
    models: ['TM-T20', 'TM-T82', 'TM-T88'],
    width: '80mm',
    notes: 'Industry standard, excellent reliability'
  },
  {
    brand: 'Star Micronics',
    models: ['TSP100', 'TSP143', 'TSP650'],
    width: '80mm',
    notes: 'Popular in POS systems, fast printing'
  },
  {
    brand: 'Bixolon',
    models: ['SRP-275', 'SRP-350', 'SRP-Q300'],
    width: '80mm',
    notes: 'Good price-performance ratio'
  },
  {
    brand: 'Citizen',
    models: ['CT-S310', 'CT-S601', 'CT-S4000'],
    width: '80mm',
    notes: 'Reliable, good for high-volume printing'
  },
  {
    brand: 'Generic',
    models: ['58mm Portable', '80mm Desktop'],
    width: '58mm / 80mm',
    notes: 'Budget-friendly options, verify compatibility'
  }
];

/**
 * Print Troubleshooting Tips
 */
export const TROUBLESHOOTING = {
  noPrint: [
    'Ensure printer is connected and powered on',
    'Check if printer drivers are installed',
    'Verify printer is set as default printer',
    'Try selecting printer manually in print dialog'
  ],
  formatting: [
    'Adjust printer width setting (58mm or 80mm)',
    'Check printer DPI settings',
    'Verify paper width matches printer configuration',
    'Adjust font size in printer settings'
  ],
  quality: [
    'Clean printer head',
    'Check paper quality',
    'Adjust print density in printer settings',
    'Replace thermal paper if faded'
  ],
  autoCut: [
    'Enable auto-cut in printer driver settings (Printer Properties > Device Settings)',
    'Check if printer supports auto-cut (most modern thermal printers do)',
    'Verify paper cutter blade is not jammed or damaged',
    'For Epson printers: Enable "Auto Cutter" in printer preferences',
    'For Star printers: Enable "Partial Cut" or "Full Cut" in driver settings',
    'Test with printer self-test to verify cutter works',
    'If using USB: Ensure proper USB connection for cutter commands'
  ]
};

export default {
  PRINTER_CONFIGS,
  DEFAULT_PRINTER_CONFIG,
  COMPANY_INFO,
  PRINT_SETTINGS,
  SUPPORTED_PRINTERS,
  TROUBLESHOOTING
};
